import { useRef, useState } from "react";
import { ApiError, type PohjaTuonti } from "./api";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Excel-tuonti ladattavalla pohjalla (T280, 13.9.2026).

   Yksi näkymä kaikille pohjallisille tuonneille (linjat, WPS; myöhemmin
   hitsiluettelo ja henkilöt). Päätös 13.9.: jokaiseen Excel-tuontiin
   pohja, johon tieto syötetään — ja pohja ja tuonti samasta
   sarakemäärittelystä (api/src/tuontipohjat.ts).

   Lataa pohja → täytä → valitse tiedosto: kuivakäynti näyttää uudet,
   jo olemassa olevat eroineen, ristiriidat ja hylätyt rivit syineen.
   Vasta «Luo» kirjoittaa. Tuonti ei muuta olemassa olevia rivejä ELLEI
   kutsuja anna `paivitysTeksti`ä ja käyttäjä rastita ruutua (G4b 19.9.2026)
   — silloin kuivakäynnissä luetellut erot kirjoitetaan, eivät muut.
   ═══════════════════════════════════════════════════════════ */

export default function PohjaTuontiPaneeli({ otsikko, selite, pohjaUrl, tuo, esto, valmis, paivitysTeksti, upotettu = false }: {
  otsikko: string;
  /** T651: paneeli napparivin alla — aina auki, ei omaa otsikkoa eikä Avaa-nappia (napparivin nappi kantaa otsikon). */
  upotettu?: boolean;
  selite: string;
  pohjaUrl: string;
  tuo: (sisalto: string, tiedosto: string, vahvista: boolean, paivita?: boolean) => Promise<PohjaTuonti>;
  /** G4b: teksti valintaruudulle joka sallii olemassa olevien päivityksen.
   *  Ilman tätä paneeli vain luo — se on yhä oletus (T280). */
  paivitysTeksti?: string;
  /** Syy miksi tiedostoa ei voi vielä tuoda (esim. kohde valitsematta). Pohjan saa silti. */
  esto?: string | null;
  valmis?: () => void;
}) {
  const [aukiTila, setAuki] = useState(false);
  const auki = upotettu || aukiTila;
  const [sisalto, setSisalto] = useState<string | null>(null);
  const [nimi, setNimi] = useState("");
  const [tulos, setTulos] = useState<PohjaTuonti | null>(null);
  const [luotu, setLuotu] = useState<number | null>(null);
  const [paivitetty, setPaivitetty] = useState<number | null>(null);
  const [paivita, setPaivita] = useState(false);
  const [tyo, setTyo] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const tiedosto = useRef<HTMLInputElement>(null);

  function nollaa() {
    setSisalto(null); setNimi(""); setTulos(null); setLuotu(null); setVirhe(null);
    if (tiedosto.current) tiedosto.current.value = "";
  }

  function valitse(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setTulos(null); setLuotu(null); setVirhe(null); setNimi(f.name);
    const lukija = new FileReader();
    lukija.onload = async () => {
      const data = String(lukija.result);
      setSisalto(data);
      setTyo(true);
      try {
        setTulos(await tuo(data, f.name.slice(0, 200), false));
      } catch (err) {
        setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("muu.pohja.luku_virhe"));
      } finally { setTyo(false); }
    };
    lukija.readAsDataURL(f);
  }

  async function vahvista() {
    if (!sisalto) return;
    setTyo(true); setVirhe(null);
    try {
      const tl = await tuo(sisalto, nimi.slice(0, 200), true, paivita);
      setLuotu(tl.luotu); setPaivitetty(tl.paivitetty ?? 0); setTulos(null);
      if (tiedosto.current) tiedosto.current.value = "";
      valmis?.();
    } catch (err) {
      setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("ltar.tallennus_virhe"));
    } finally { setTyo(false); }
  }

  const eroja = tulos?.olemassa_esikatselu.filter((o) => o.erot.length > 0) ?? [];

  return (
    <section className="panel" data-pohjatuonti={otsikko}>
      {!upotettu && <div className="panel__head">
        <h2>{otsikko}</h2>
        <button className="nappi nappi--kevyt" onClick={() => { if (auki) nollaa(); setAuki(!auki); }}>
          {auki ? t("hl.sulje") : t("qr.avaa")}
        </button>
      </div>}

      {auki && (
        <div style={{ padding: "4px 12px 12px" }}>
          <p className="hint">{selite}</p>
          <p style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <a className="nappi nappi--kevyt" href={pohjaUrl}>{t("muu.pohja.lataa_pohja")}</a>
            {esto
              ? <span className="dim">{esto}</span>
              : <input ref={tiedosto} type="file" accept=".xlsx" onChange={valitse} disabled={tyo} />}
          </p>

          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
          {tyo && <p className="dim">{t("muu.pohja.kasitellaan")}</p>}

          {tulos && (
            <div data-kuivakaynti="">
              <p>
                <strong>{t("muu.pohja.uutta", { n: tulos.uusia })}</strong> · {t("muu.pohja.jo_olemassa", { n: tulos.olemassa })}
                {" · "}{t("muu.pohja.ristiriitaa", { n: tulos.ristiriitoja })} · {t("muu.pohja.hylattya", { n: tulos.hylattyja })}
                {tulos.tyhjia > 0 && <span className="dim"> · {t("muu.pohja.tyhjaa", { n: tulos.tyhjia })}</span>}
              </p>

              {/* Tuntematon sarake ei kaada, mutta sitä ei lueta — sanotaan nimeltä,
                  ettei kirjoitusvirhe otsikossa hukkaa saraketta hiljaa. */}
              {tulos.tuntemattomat.length > 0 && (
                <p className="huom">{t("muu.pohja.tuntemattomat", { s: tulos.tuntemattomat.join(", ") })}</p>
              )}

              {(tulos.huomautukset?.length ?? 0) > 0 && (
                <ul className="kohdelista">
                  {tulos.huomautukset!.map((h) => <li key={h}><span>{h}</span></li>)}
                </ul>
              )}

              {tulos.hylatyt.length > 0 && (
                <>
                  <p className="huom huom--vika">{t("muu.pohja.hylatyt")}</p>
                  <ul className="kohdelista">
                    {tulos.hylatyt.map((h) => (
                      <li key={h.rivi}><span className="mono">{t("muu.pohja.rivi_n", { n: h.rivi })}</span><span>{h.syyt.join(" · ")}</span></li>
                    ))}
                  </ul>
                </>
              )}

              {tulos.ristiriidat.length > 0 && (
                <>
                  <p className="huom">{t("muu.pohja.ristiriidat")}</p>
                  <ul className="kohdelista">
                    {tulos.ristiriidat.map((r) => (
                      <li key={r.rivi}><span className="mono">{t("muu.pohja.rivi_n", { n: r.rivi })} · {r.tunnus}</span><span>{r.syy}</span></li>
                    ))}
                  </ul>
                </>
              )}

              {eroja.length > 0 && (
                <>
                  <p className="huom">{t("muu.pohja.erot")}</p>
                  <div className="scroll">
                    <table className="tbl">
                      <thead><tr><th className="th--vasen">{t("muu.pohja.rivi")}</th><th className="th--vasen">{t("muu.pohja.tunnus")}</th>
                        <th className="th--vasen">{t("muu.pohja.kentta")}</th><th className="th--vasen">{t("muu.pohja.jarjestelmassa")}</th><th className="th--vasen">{t("muu.pohja.tiedostossa")}</th></tr></thead>
                      <tbody>
                        {eroja.flatMap((o) => o.erot.map((e, i) => (
                          <tr key={`${o.rivi}-${i}`}>
                            <td className="mono">{o.rivi}</td><td className="mono">{o.tunnus}</td>
                            <td>{e.kentta}</td><td className="dim">{e.sampo}</td><td>{e.tiedosto}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {tulos.uudet_esikatselu.length > 0 && (
                <div className="scroll">
                  <table className="tbl">
                    <thead><tr><th className="th--vasen">{t("muu.pohja.rivi")}</th><th className="th--vasen">{t("muu.pohja.luodaan")}</th><th className="th--vasen">{t("muu.pohja.tiedostossa")}</th></tr></thead>
                    <tbody>
                      {tulos.uudet_esikatselu.map((u) => (
                        <tr key={u.rivi}><td className="mono">{u.rivi}</td><td className="mono">{u.tunnus}</td><td className="dim">{u.kuvaus}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  {tulos.uusia > tulos.uudet_esikatselu.length && (
                    <p className="dim">{t("muu.pohja.esikatselu", { n: tulos.uudet_esikatselu.length, m: tulos.uusia })}</p>
                  )}
                </div>
              )}

              {/* Ei luotavaa on tulos siinä missä luotavat: se kertoo että tiedosto luettiin. */}
              {paivitysTeksti && (tulos.paivitettavia ?? 0) > 0 && (
                <p className="lomake__kentta">
                  <label data-paivitys="">
                    <input type="checkbox" checked={paivita} onChange={(e) => setPaivita(e.target.checked)} />
                    {" "}{paivitysTeksti} ({tulos.paivitettavia})
                  </label>
                  {/* Tyhjä solu ei muuta mitään: tyhjästä ei synny eroa eikä päivitystä. */}
                  <span className="vihje"> {t("muu.pohja.vain_erot")}</span>
                </p>
              )}

              {/* Ei luotavaa on tulos siinä missä luotavat: se kertoo että tiedosto luettiin. */}
              {tulos.uusia === 0 && !(paivita && (tulos.paivitettavia ?? 0) > 0)
                ? <p>{t("muu.pohja.ei_luotavaa")} — {tulos.olemassa > 0 ? t("muu.pohja.kaikki_olemassa") : t("muu.pohja.ei_kelvollisia")}</p>
                : <p><button className="nappi nappi--paa" disabled={tyo} onClick={() => void vahvista()} data-vahvista="">
                    {tulos.uusia > 0 ? t("muu.pohja.luo_uudet", { n: tulos.uusia }) : ""}
                    {tulos.uusia > 0 && paivita && (tulos.paivitettavia ?? 0) > 0 ? t("muu.pohja.ja") : ""}
                    {paivita && (tulos.paivitettavia ?? 0) > 0 ? t("muu.pohja.paivita", { n: tulos.paivitettavia ?? 0 }) : ""}
                  </button></p>}
            </div>
          )}

          {luotu !== null && (
            <p className="kuittaus-ok" data-tuonti-tulos="">
              ✓ {t("muu.pohja.luotu", { n: luotu })} {paivitetty ? t("muu.pohja.paivitetty", { n: paivitetty }) : t("muu.pohja.ei_muutettu")}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
